import { Button } from '@/components/ui/button';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';
import {
	Sheet,
	SheetContent,
	SheetDescription,
	SheetHeader,
	SheetTitle,
	SheetTrigger,
} from '@/components/ui/sheet';
import copy from 'copy-to-clipboard';
import { useState } from 'react';
// import { useSummarizeGroupCalculationsLazyQuery } from '../gql/_gen_/group-pricing.gql';

interface DebugViewProps {
	data: unknown;
	label: string;
}

export function DebugInfo({ data, label }: DebugViewProps) {
	const [copyMsg, setCopyMsg] = useState('Copy');
	// const [summary, setSummary] = useState<string | null>(null);
	const getExplanation = (data: any) => {
		if (!data || typeof data !== 'object') return null;

		let recommendation = data.recommendation || data.status || '';
		if (!recommendation && typeof data.takeGroup === 'boolean') {
			recommendation = data.takeGroup ? 'take' : 'reconsider';
		}
		const {
			netBenefit,
			requiredProfitMargin: requiredMargin,
			actualProfitMargin: actualMargin,
			requiredProfit,
		} = data;

		const hasRequiredMargin = !!requiredMargin;
		const hasActualMargin = !!actualMargin;
		const hasNetBenefit = !!netBenefit;
		const hasRequiredProfit = !!requiredProfit;

		if (recommendation.toLowerCase().includes('reconsider')) {
			if (hasRequiredMargin && hasActualMargin && hasRequiredProfit) {
				return (
					<>
						<span className='tw-font-semibold tw-text-red-600'>
							Reconsider group:
						</span>{' '}
						Your current profit margin requirement (<b>{requiredMargin}%</b>)
						exceeds this group's potential (<b>{actualMargin}%</b>). Net benefit
						would be <b>${Math.round(netBenefit)}</b> vs. required{' '}
						<b>${requiredProfit}</b>.
						<br />
						{actualMargin < requiredMargin && (
							<span>
								This group would meet requirements with a <b>{actualMargin}%</b>{' '}
								profit margin or lower.
							</span>
						)}
					</>
				);
			}
			if (hasNetBenefit && hasRequiredProfit) {
				let suggestedMargin = null;
				if (netBenefit > 0) {
					suggestedMargin = Math.floor((requiredProfit / netBenefit) * 100);
				}
				const neededIncrease = requiredProfit - netBenefit;
				return (
					<>
						<span className='tw-font-semibold tw-text-red-600'>
							Reconsider group:
						</span>{' '}
						Net benefit (<b>${netBenefit.toLocaleString()}</b>) is below your
						required profit (<b>${requiredProfit.toLocaleString()}</b>).
						{suggestedMargin && suggestedMargin < 100 && (
							<>
								<br />
								This group would meet requirements with a profit margin of{' '}
								<b>{suggestedMargin}%</b> or lower.
							</>
						)}
						{neededIncrease > 0 && neededIncrease <= 1000 && (
							<>
								<br />
								Consider reducing your required profit margin to allow this
								group to qualify.
							</>
						)}
					</>
				);
			}
			return 'Reconsider group: No detailed profit/profit margin data available.';
		}
		if (recommendation.toLowerCase().includes('take')) {
			if (hasRequiredMargin && hasActualMargin && hasNetBenefit) {
				return (
					<>
						<span className='tw-font-semibold tw-text-green-700'>
							Take group:
						</span>{' '}
						This group meets your profit margin requirement (
						<b>{requiredMargin}%</b>) with an actual margin of{' '}
						<b>{actualMargin}%</b>. Net benefit:{' '}
						<b>${netBenefit.toLocaleString()}</b>.<br />
						{actualMargin > requiredMargin && (
							<span>
								Group exceeds requirements by{' '}
								<b>{(actualMargin - requiredMargin).toFixed(1)}%</b>.
							</span>
						)}
					</>
				);
			}
			if (hasNetBenefit && hasRequiredProfit) {
				return (
					<>
						<span className='tw-font-semibold tw-text-green-700'>
							Take group:
						</span>{' '}
						Net benefit (<b>${netBenefit.toLocaleString()}</b>) meets or exceeds
						your required profit (<b>${requiredProfit.toLocaleString()}</b>).
					</>
				);
			}
			return 'Take group: No detailed profit/profit margin data available.';
		}
		return null;
	};
	const summary = getExplanation(data);
	// const [summarizeGroupCalculations, { loading }] =
	// 	useSummarizeGroupCalculationsLazyQuery();

	const handleCopy = () => {
		copy(JSON.stringify({ data }, null, 2));
		setCopyMsg('Copied!');
		setTimeout(() => {
			setCopyMsg('Copy');
		}, 2000);
	};

	// const handleSummarize = async () => {
	// 	try {
	// 		await summarizeGroupCalculations({
	// 			variables: {
	// 				data: JSON.stringify(data),
	// 			},
	// 			onCompleted: (result) => {
	// 				if (result.summarizeGroupCalculations) {
	// 					setSummary(result.summarizeGroupCalculations);
	// 				} else {
	// 					setSummary('No summary available.');
	// 				}
	// 			},
	// 			onError: (error) => {
	// 				console.error('Error summarizing calculations:', error);
	// 				setSummary('Error summarizing calculations.');
	// 			},
	// 		});
	// 	} catch (error) {
	// 		console.error('Error summarizing calculations:', error);
	// 	}
	// };

	return (
		<Sheet>
			<SheetTrigger>{label}</SheetTrigger>
			<SheetContent>
				<SheetHeader>
					<SheetTitle>Debug Data</SheetTitle>
					<SheetDescription>
						<div className='tw-flex tw-flex-col tw-gap-2'>
							<Button
								onClick={handleCopy}
								variant='outline'
								size='sm'
								className='tw-self-end'
							>
								{copyMsg}
							</Button>
							{/* // Hide the summarize button for now until we can address its brittleness with input data structure. DW */}
							{/* {data && JSON.stringify(data).length > 10 && (
								<Button
									onClick={handleSummarize}
									variant='outline'
									size='sm'
									disabled={loading}
									className='tw-self-end'
								>
									{loading ? 'Summarizing...' : 'Summarize'}
								</Button>
							)} */}
							<div className='tw-w-[280px] tw-mt-2 tw-p-4 tw-border tw-rounded-md tw-bg-gray-50'>
								<h3 className='tw-text-base tw-font-semibold tw-mb-2'>
									Explanation of Recommendation
								</h3>
								{summary ? (
									typeof summary === 'string' ? (
										<p className='tw-text-sm tw-leading-relaxed tw-whitespace-pre-line'>
											{summary}
										</p>
									) : (
										<span className='tw-text-sm tw-leading-relaxed tw-whitespace-pre-line'>
											{summary}
										</span>
									)
								) : (
									<p className='tw-text-sm tw-leading-relaxed tw-whitespace-pre-line'>
										No recommendation explanation available for this data.
									</p>
								)}
							</div>
							<ScrollArea className='tw-h-[600px] tw-w-[300px] tw-rounded-md tw-border tw-p-4'>
								<pre>{JSON.stringify(data, null, 2)}</pre>
								<ScrollBar orientation='horizontal' />
							</ScrollArea>
						</div>
					</SheetDescription>
				</SheetHeader>
			</SheetContent>
		</Sheet>
	);
}
